import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, DOCUMENT } from '@angular/core';

import { environment } from '../../../environments/environment';

export interface IntercomBootInput {
  custom_launcher_selector?: string;
  user_id?: string;
  email?: string;
  /**
   * If no app_id is passed, the value from the environment config is used.
   */
  app_id?: string;
  [propName: string]: any;
}

declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: any;
  }
}

/**
 * Minimal, application-owned replacement for the unmaintained `ng-intercom`
 * package (last published for View Engine; incompatible with Angular's Ivy
 * template type-checking from Angular 16 onward — see
 * student/docs/adr/0004-legacy-integration-replacements.md). Reimplements
 * only the `boot`/`shutdown` surface this app actually uses, preserving the
 * exact widget-loading behavior (lazy script injection keyed off `appId`,
 * queueing calls made before the script loads) of the original service.
 */
@Injectable({ providedIn: 'root' })
export class Intercom {
  private appId: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Record<string, unknown>,
    @Inject(DOCUMENT) private document: Document
  ) {}

  boot(intercomData: IntercomBootInput = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const appId = intercomData.app_id || environment.intercom.appId;
    this.appId = appId;

    this.loadIntercom(appId, () => {
      this.callIntercom('boot', { ...intercomData, app_id: appId });
    });
  }

  shutdown(): void {
    this.callIntercom('shutdown');
  }

  private callIntercom(fn: string, ...args: any[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const win = window as Window;
    if (typeof win.Intercom === 'function') {
      win.Intercom(fn, ...args);
    }
  }

  private loadIntercom(appId: string, afterLoadCallback: () => void): void {
    const win = window as Window;
    win.intercomSettings = { app_id: appId };

    if (typeof win.Intercom === 'function') {
      win.Intercom('reattach_activator');
      win.Intercom('update', win.intercomSettings);
      afterLoadCallback();
      return;
    }

    // Standard Intercom "install script" stub: queues calls made before the
    // real widget script (loaded below) takes over `window.Intercom` and
    // drains the queue itself.
    const stub: any = (...args: any[]) => stub.c(args);
    stub.q = [];
    stub.c = (args: any[]) => stub.q.push(args);
    win.Intercom = stub;

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    script.addEventListener('load', afterLoadCallback, false);
    this.document.head.appendChild(script);
  }
}
